import {
  createContext,
  useContext,
  PropsWithChildren,
  useEffect,
  useCallback,
  useReducer,
  useMemo,
  useRef,
} from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PaymentMethodInfo, PaymentTerm, WaiverData } from '@la/types';
import { getLAHostnameParts } from '@la/utilities';
import { ToastVariant } from 'components/Toast/Toast';
import {
  useDeleteCartItemMutation,
  useGetCheckoutSummaryBFFQuery,
  useUpdateCheckoutSummaryBFFMutation,
} from 'redux/services/checkoutApi';
import {
  CartDataFromUpdate,
  CheckoutSummaryData,
  CombinedCartItemData,
  DiscountCode,
  PaymentMethod,
  PaymentPlanSummary,
  SingleUseCard,
} from 'domains/ConsolidatedCheckout/Checkout.types';
import { isSingleUseCard } from 'domains/ConsolidatedCheckout/PaymentMethodCard/PaymentMethodCard';
import { MessagingContext } from '../MessagingContext';

export type ValidationResult = {
  isValid: boolean;
  reason?: string;
};

export type DiscountCodeToastType = 'add' | 'duplicate' | 'remove';

export type CheckoutFieldErrorStates = {
  hasESignatureError?: boolean;
  hasPaymentMethodError?: boolean;
  hasPaymentPolicyError?: boolean;
  hasSubmissionError?: boolean;
};

export type ConsolidatedCheckoutData = {
  cartItems: CombinedCartItemData[];
  cartUuid?: string;
  checkoutErrorsAreVisible: boolean;
  checkoutFieldErrorStates: CheckoutFieldErrorStates;
  currency?: string;
  discountCodeCodes: string[];
  discountCodeErrors: string[];
  discountCodes: DiscountCode[];
  dueToday: string;
  eSignature?: string;
  hasAgreedToAutopay: boolean;
  hasAgreedToPaymentPolicy: boolean;
  hasAutopayPaymentOptionSelected: boolean;
  isLoading: string[];
  paymentWaiver?: WaiverData;
  selectedPaymentMethod: PaymentMethod;
  selectedPaymentPlans?: PaymentPlanSummary[];
  storedPaymentMethods: PaymentMethodInfo;
  submissionErrors: string[];
  submissionErrorsAreVisible: boolean;
  subtotal?: string;
  taxAmount?: string;
  temporaryPaymentMethod?: SingleUseCard;
  totalAmount?: string;
  transactionFees: string;
  updateCartItems: (updatedCartItems: CombinedCartItemData[]) => void;
  updateCheckoutErrorsAreVisible: (checkoutErrorsAreVisible: boolean) => void;
  updateDataAfterCartItemDeletion: (
    updatedDataAfterCartItemDeletion: CartDataFromUpdate
  ) => void;
  updateDataAfterDiscountCodesUpdate: (
    updatedDataAfterDiscountCodesUpdate: CartDataFromUpdate
  ) => void;
  updateDataAfterPaymentMethodSelection: (
    updatedDataAfterPaymentMethodSelectionUpdate: CartDataFromUpdate
  ) => void;
  updateDataAfterPaymentPlanSelection: (
    updatedDataAfterPaymentPlanSelectionUpdate: CartDataFromUpdate
  ) => void;
  updateDiscountCodeErrors: (errorMessages: string[]) => void;
  updateESignature: (eSignature?: string) => void;
  updateESignatureError: (displayError: boolean) => void;
  updateHasAgreedToAutopay: (hasAgreedToAutopay: boolean) => void;
  updateHasAgreedToPaymentPolicy: (hasAgreedToPaymentPolicy: boolean) => void;
  updatePaymentMethodErrors: (displayError: boolean) => void;
  updatePaymentWaiver: (paymentWaiver?: WaiverData) => void;
  updatePaymentWaiverErrors: (displayError: boolean) => void;
  updateSelectedPaymentMethod: (paymentMethod: PaymentMethod) => void;
  updateStoredPaymentMethods: (storedPaymentMethods: PaymentMethodInfo) => void;
  updateSubmissionErrors: (errorMessages: string[]) => void;
  updateSubmissionErrorsAreVisible: (displayError: boolean) => void;
  updateTemporaryPaymentMethod: (singleUseCard: SingleUseCard) => void;
};

const { subdomain } = getLAHostnameParts();

export const consolidatedCheckoutDefaults: ConsolidatedCheckoutData = {
  cartItems: [],
  cartUuid: '',
  checkoutErrorsAreVisible: false,
  checkoutFieldErrorStates: {
    hasESignatureError: false,
    hasPaymentMethodError: false,
    hasPaymentPolicyError: false,
    hasSubmissionError: false,
  },
  currency: 'USD',
  discountCodes: [],
  discountCodeErrors: [],
  discountCodeCodes: [],
  dueToday: '0.00',
  eSignature: undefined,
  hasAgreedToAutopay: false,
  hasAgreedToPaymentPolicy: false,
  hasAutopayPaymentOptionSelected: false,
  isLoading: ['page'],
  paymentWaiver: undefined,
  selectedPaymentMethod: null,
  selectedPaymentPlans: [],
  storedPaymentMethods: {
    paymentOptionsCount: 0,
    storedBankAccounts: [],
    storedCreditCards: [],
  },
  submissionErrors: [],
  submissionErrorsAreVisible: false,
  subtotal: '0.00',
  taxAmount: undefined,
  temporaryPaymentMethod: undefined,
  totalAmount: '0.00',
  transactionFees: '0.00',
  updateCartItems: () => {},
  updateCheckoutErrorsAreVisible: () => {},
  updateDataAfterCartItemDeletion: () => {},
  updateDataAfterDiscountCodesUpdate: () => {},
  updateDataAfterPaymentMethodSelection: () => {},
  updateDataAfterPaymentPlanSelection: () => {},
  updateDiscountCodeErrors: () => {},
  updateESignature: () => {},
  updateESignatureError: () => {},
  updateHasAgreedToAutopay: () => {},
  updateHasAgreedToPaymentPolicy: () => {},
  updatePaymentMethodErrors: () => {},
  updatePaymentWaiver: () => {},
  updatePaymentWaiverErrors: () => {},
  updateSelectedPaymentMethod: () => {},
  updateStoredPaymentMethods: () => {},
  updateSubmissionErrors: () => {},
  updateSubmissionErrorsAreVisible: () => {},
  updateTemporaryPaymentMethod: () => {},
};

function checkoutCartReducer(
  state: ConsolidatedCheckoutData,
  action: { type: string; payload: any }
): ConsolidatedCheckoutData {
  const { type, payload } = action;
  switch (type) {
    // Set Initial State
    case 'SET_INITIAL_LOAD_STATE': {
      return { ...state, ...payload };
    }

    // Payment Method
    case 'UPDATE_STORED_PAYMENT_METHODS':
      return {
        ...state,
        storedPaymentMethods: payload,
      };
    case 'UPDATE_SELECTED_PAYMENT_METHOD':
      return { ...state, selectedPaymentMethod: payload };
    case 'UPDATE_TEMPORARY_PAYMENT_METHOD':
      return { ...state, temporaryPaymentMethod: payload };

    // Payment Plan Selection
    case 'UPDATE_SELECTED_PAYMENT_PLANS':
      return { ...state, selectedPaymentPlans: payload };

    // Discount Codes
    case 'UPDATE_DISCOUNT_CODES':
      return {
        ...state,
        discountCodes: payload,
      };
    case 'UPDATE_DISCOUNT_CODE_CODES':
      return {
        ...state,
        discountCodeCodes: payload,
      };

    // Error Messages
    case 'UPDATE_DISCOUNT_CODE_ERRORS':
      return {
        ...state,
        discountCodeErrors: payload,
      };
    case 'UPDATE_SUBMISSION_ERRORS':
      return {
        ...state,
        submissionErrors: payload,
      };
    case 'UPDATE_CHECKOUT_ERRORS_ARE_VISIBLE':
      return {
        ...state,
        checkoutErrorsAreVisible: payload,
      };
    case 'UPDATE_SUBMISSION_ERRORS_ARE_VISIBLE':
      return {
        ...state,
        submissionErrorsAreVisible: payload,
      };
    case 'UPDATE_HAS_AGREED_TO_AUTOPAY':
      return { ...state, hasAgreedToAutopay: payload };
    case 'UPDATE_HAS_PAYMENT_PLAN_WITH_AUTOPAY':
      return { ...state, hasAutopayPaymentOptionSelected: payload };
    case 'UPDATE_HAS_AGREED_TO_PAYMENT_POLICY':
      return { ...state, hasAgreedToPaymentPolicy: payload };
    case 'SET_CART_UUID':
      return { ...state, cartUuid: payload };
    case 'SET_CURRENCY':
      return { ...state, currency: payload };
    case 'UPDATE_CART_ITEMS':
      return { ...state, cartItems: payload };
    case 'UPDATE_DUE_TODAY':
      return { ...state, dueToday: payload };
    case 'UPDATE_E_SIGNATURE':
      return { ...state, eSignature: payload };
    case 'UPDATE_SUBTOTAL':
      return { ...state, subtotal: payload };
    case 'UPDATE_TAX_AMOUNT':
      return { ...state, taxAmount: payload };
    case 'UPDATE_TOTAL_AMOUNT':
      return { ...state, totalAmount: payload };
    case 'UPDATE_TRANSACTION_FEES':
      return { ...state, transactionFees: payload };
    case 'UPDATE_PAYMENT_WAIVER':
      return { ...state, paymentWaiver: payload };
    case 'UPDATE_HAS_E_SIGNATURE_ERROR':
      return {
        ...state,
        checkoutFieldErrorStates: {
          ...state.checkoutFieldErrorStates,
          hasESignatureError: payload,
          hasSubmissionError: false,
        },
      };
    case 'UPDATE_HAS_PAYMENT_METHOD_ERRORS':
      return {
        ...state,
        checkoutFieldErrorStates: {
          ...state.checkoutFieldErrorStates,
          hasPaymentMethodError: payload,
          hasSubmissionError: false,
        },
      };
    case 'UPDATE_HAS_PAYMENT_POLICY_ERRORS':
      return {
        ...state,
        checkoutFieldErrorStates: {
          ...state.checkoutFieldErrorStates,
          hasPaymentPolicyError: payload,
          hasSubmissionError: false,
        },
      };
    case 'UPDATE_HAS_SUBMISSION_ERRORS':
      return {
        ...state,
        checkoutFieldErrorStates: {
          ...state.checkoutFieldErrorStates,
          hasSubmissionError: payload,
        },
      };
    default:
      return state;
  }
}

const ConsolidatedCheckoutContext = createContext<ConsolidatedCheckoutData>(
  consolidatedCheckoutDefaults
);

const ConsolidatedCheckoutProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(
    checkoutCartReducer,
    consolidatedCheckoutDefaults
  );

  const hasSummaryDataRequestSucceeded = useRef(false);
  const { data: checkoutSummary } = useGetCheckoutSummaryBFFQuery(
    {
      subdomain: subdomain,
    },
    {
      skip: hasSummaryDataRequestSucceeded.current,
    }
  );

  const setCheckoutSummary = useCallback(
    (checkoutSummaryData: CheckoutSummaryData) => {
      dispatch({
        type: 'SET_INITIAL_LOAD_STATE',
        payload: { ...checkoutSummaryData, isLoading: [] },
      });
    },
    []
  );

  const updateCartItems = useCallback(
    (updatedCartItems: CombinedCartItemData[]) => {
      dispatch({ type: 'UPDATE_CART_ITEMS', payload: updatedCartItems });
    },
    []
  );

  const updateStoredPaymentMethods = useCallback(
    (storedPaymentMethods: PaymentMethodInfo) => {
      dispatch({
        type: 'UPDATE_STORED_PAYMENT_METHODS',
        payload: storedPaymentMethods,
      });
    },
    []
  );

  const updateTemporaryPaymentMethod = useCallback(
    (temporaryPaymentMethod: SingleUseCard) => {
      dispatch({
        type: 'UPDATE_TEMPORARY_PAYMENT_METHOD',
        payload: temporaryPaymentMethod,
      });
    },
    []
  );

  /* Data Updates (Following Submission) */
  const updateDataAfterCartItemDeletion = useCallback(
    async (updatedData: CartDataFromUpdate) => {
      const { cartItems, dueToday, taxAmount, totalAmount, transactionFees } =
        updatedData;
      const autopayRequired = cartItemsIncludeAutopayPaymentPlan(cartItems);

      dispatch({
        type: 'UPDATE_CART_ITEMS',
        payload: cartItems,
      });
      dispatch({
        type: 'UPDATE_DUE_TODAY',
        payload: dueToday,
      });
      dispatch({
        type: 'UPDATE_TAX_AMOUNT',
        payload: taxAmount,
      });
      dispatch({
        type: 'UPDATE_TOTAL_AMOUNT',
        payload: totalAmount,
      });
      dispatch({
        type: 'UPDATE_TRANSACTION_FEES',
        payload: transactionFees,
      });
      dispatch({
        type: 'UPDATE_HAS_PAYMENT_PLAN_WITH_AUTOPAY',
        payload: autopayRequired,
      });
    },
    []
  );

  const updateDataAfterDiscountCodesUpdate = useCallback(
    async (updatedData: CartDataFromUpdate) => {
      const {
        cartItems,
        discountCodes,
        dueToday,
        taxAmount,
        totalAmount,
        transactionFees,
      } = updatedData;
      const discountCodeCodes: string[] =
        getUniqueDiscountCodeCodes(discountCodes);

      dispatch({
        type: 'UPDATE_CART_ITEMS',
        payload: cartItems,
      });
      dispatch({
        type: 'UPDATE_DUE_TODAY',
        payload: dueToday,
      });
      dispatch({
        type: 'UPDATE_DISCOUNT_CODES',
        payload: discountCodes,
      });
      dispatch({
        type: 'UPDATE_DISCOUNT_CODE_CODES',
        payload: discountCodeCodes,
      });
      dispatch({
        type: 'UPDATE_DISCOUNT_CODE_ERRORS',
        payload: [],
      });
      dispatch({
        type: 'UPDATE_TAX_AMOUNT',
        payload: taxAmount,
      });
      dispatch({
        type: 'UPDATE_TOTAL_AMOUNT',
        payload: totalAmount,
      });
      dispatch({
        type: 'UPDATE_TRANSACTION_FEES',
        payload: transactionFees,
      });
    },
    []
  );

  const updateDataAfterPaymentMethodSelection = useCallback(
    (updatedData: CartDataFromUpdate) => {
      const { cartItems, dueToday, taxAmount, totalAmount, transactionFees } =
        updatedData;
      dispatch({
        type: 'UPDATE_CART_ITEMS',
        payload: cartItems,
      });
      dispatch({
        type: 'UPDATE_DUE_TODAY',
        payload: dueToday,
      });
      dispatch({
        type: 'UPDATE_TAX_AMOUNT',
        payload: taxAmount,
      });
      dispatch({
        type: 'UPDATE_TOTAL_AMOUNT',
        payload: totalAmount,
      });
      dispatch({
        type: 'UPDATE_TRANSACTION_FEES',
        payload: transactionFees,
      });
    },
    []
  );

  const updateDataAfterPaymentPlanSelection = useCallback(
    (updatedData: CartDataFromUpdate) => {
      const { cartItems, dueToday, taxAmount, totalAmount, transactionFees } =
        updatedData;
      const autopayRequired = cartItemsIncludeAutopayPaymentPlan(cartItems);

      dispatch({
        type: 'UPDATE_CART_ITEMS',
        payload: cartItems,
      });
      dispatch({
        type: 'UPDATE_DUE_TODAY',
        payload: dueToday,
      });
      dispatch({
        type: 'UPDATE_TAX_AMOUNT',
        payload: taxAmount,
      });
      dispatch({
        type: 'UPDATE_TOTAL_AMOUNT',
        payload: totalAmount,
      });
      dispatch({
        type: 'UPDATE_TRANSACTION_FEES',
        payload: transactionFees,
      });
      dispatch({
        type: 'UPDATE_HAS_PAYMENT_PLAN_WITH_AUTOPAY',
        payload: autopayRequired,
      });
    },
    []
  );
  /* */

  /* Update Errors */
  const updateDiscountCodeErrors = useCallback((errorMessages: string[]) => {
    dispatch({
      type: 'UPDATE_DISCOUNT_CODE_ERRORS',
      payload: errorMessages,
    });
  }, []);

  const updateESignatureError = useCallback((displayError: boolean) => {
    dispatch({
      type: 'UPDATE_HAS_E_SIGNATURE_ERROR',
      payload: displayError,
    });
  }, []);

  const updatePaymentMethodErrors = useCallback((displayError: boolean) => {
    dispatch({
      type: 'UPDATE_HAS_PAYMENT_METHOD_ERRORS',
      payload: displayError,
    });
  }, []);

  const updatePaymentWaiverErrors = useCallback((displayError: boolean) => {
    dispatch({
      type: 'UPDATE_HAS_PAYMENT_POLICY_ERRORS',
      payload: displayError,
    });
    if (displayError) {
      dispatch({
        type: 'UPDATE_SUBMISSION_ERRORS_ARE_VISIBLE',
        payload: true,
      });
    }
  }, []);

  const updateSubmissionErrors = useCallback((submissionErrors: string[]) => {
    dispatch({ type: 'UPDATE_SUBMISSION_ERRORS', payload: submissionErrors });
    dispatch({
      type: 'UPDATE_HAS_SUBMISSION_ERRORS',
      payload: submissionErrors.length > 0,
    });
  }, []);
  /* */

  /* Update Error Visibility */
  const updateCheckoutErrorsAreVisible = useCallback(
    (checkoutErrorsAreVisible: boolean) => {
      dispatch({
        type: 'UPDATE_CHECKOUT_ERRORS_ARE_VISIBLE',
        payload: checkoutErrorsAreVisible,
      });
    },
    []
  );

  const updateSubmissionErrorsAreVisible = useCallback(
    (areSubmissionErrorsVisible: boolean) => {
      if (!areSubmissionErrorsVisible) {
        updateSubmissionErrors([]);
      }
      dispatch({
        type: 'UPDATE_SUBMISSION_ERRORS_ARE_VISIBLE',
        payload: areSubmissionErrorsVisible,
      });
    },
    [updateSubmissionErrors]
  );
  /* */

  const updateSelectedPaymentMethod = useCallback(
    (selectedPaymentMethod: PaymentMethod) => {
      dispatch({
        type: 'UPDATE_SELECTED_PAYMENT_METHOD',
        payload: selectedPaymentMethod,
      });
      updatePaymentMethodErrors(
        !selectedPaymentMethod ||
          (!selectedPaymentMethod.paymentMethodId &&
            state.hasAutopayPaymentOptionSelected)
      );
    },
    [state.hasAutopayPaymentOptionSelected, updatePaymentMethodErrors]
  );

  const setSelectedPaymentMethodToDefault = useCallback(
    (storedPaymentMethods: PaymentMethodInfo) => {
      const defaultPaymentMethod =
        getDefaultPaymentMethod(storedPaymentMethods);
      if (defaultPaymentMethod) {
        updateSelectedPaymentMethod(defaultPaymentMethod);
      }
    },
    [updateSelectedPaymentMethod]
  );

  const updatePaymentWaiver = useCallback((paymentWaiver?: WaiverData) => {
    dispatch({ type: 'UPDATE_PAYMENT_WAIVER', payload: paymentWaiver });
  }, []);

  /* Agreements */
  const updateHasAgreedToAutopay = useCallback(
    (hasAgreedToAutopay: boolean) => {
      dispatch({
        type: 'UPDATE_HAS_AGREED_TO_AUTOPAY',
        payload: hasAgreedToAutopay,
      });
      if (state.hasAutopayPaymentOptionSelected) {
        updatePaymentMethodErrors(!hasAgreedToAutopay);
      } else {
        updatePaymentMethodErrors(false);
      }
    },
    [state.hasAutopayPaymentOptionSelected, updatePaymentMethodErrors]
  );

  const updateESignature = useCallback(
    (eSignature?: string): void => {
      dispatch({ type: 'UPDATE_E_SIGNATURE', payload: eSignature });
      if (!eSignature || eSignature.trim() === '') {
        updateESignatureError(true);
      } else {
        updateESignatureError(false);
      }
    },
    [updateESignatureError]
  );

  const updateHasAgreedToPaymentPolicy = useCallback(
    (hasAgreedToPaymentPolicy: boolean) => {
      dispatch({
        type: 'UPDATE_HAS_AGREED_TO_PAYMENT_POLICY',
        payload: hasAgreedToPaymentPolicy,
      });
      if (state.paymentWaiver) {
        updatePaymentWaiverErrors(!hasAgreedToPaymentPolicy);
      } else {
        updatePaymentWaiverErrors(false);
      }
    },
    [state.paymentWaiver, updatePaymentWaiverErrors]
  );
  /* */

  useEffect(() => {
    if (checkoutSummary && !hasSummaryDataRequestSucceeded.current) {
      hasSummaryDataRequestSucceeded.current = true;
      setCheckoutSummary(checkoutSummary);
      setSelectedPaymentMethodToDefault(checkoutSummary.storedPaymentMethods);
    }
  }, [checkoutSummary, setCheckoutSummary, setSelectedPaymentMethodToDefault]);

  const memoizedState = useMemo(
    () => ({
      ...state,
      updateCartItems,
      updateCheckoutErrorsAreVisible,
      updateDataAfterCartItemDeletion,
      updateDataAfterDiscountCodesUpdate,
      updateDataAfterPaymentMethodSelection,
      updateDataAfterPaymentPlanSelection,
      updateDiscountCodeErrors,
      updateESignature,
      updateESignatureError,
      updateHasAgreedToAutopay,
      updateHasAgreedToPaymentPolicy,
      updatePaymentMethodErrors,
      updatePaymentWaiver,
      updatePaymentWaiverErrors,
      updateSelectedPaymentMethod,
      updateStoredPaymentMethods,
      updateSubmissionErrors,
      updateSubmissionErrorsAreVisible,
      updateTemporaryPaymentMethod,
    }),
    [
      state,
      updateCartItems,
      updateCheckoutErrorsAreVisible,
      updateDataAfterCartItemDeletion,
      updateDataAfterDiscountCodesUpdate,
      updateDataAfterPaymentMethodSelection,
      updateDataAfterPaymentPlanSelection,
      updateDiscountCodeErrors,
      updateESignature,
      updateESignatureError,
      updateHasAgreedToAutopay,
      updateHasAgreedToPaymentPolicy,
      updatePaymentMethodErrors,
      updatePaymentWaiver,
      updatePaymentWaiverErrors,
      updateSelectedPaymentMethod,
      updateStoredPaymentMethods,
      updateSubmissionErrors,
      updateSubmissionErrorsAreVisible,
      updateTemporaryPaymentMethod,
    ]
  );

  return (
    <ConsolidatedCheckoutContext.Provider value={memoizedState}>
      {children}
    </ConsolidatedCheckoutContext.Provider>
  );
};

const useConsolidatedCheckout = () => {
  const { ngESignature } = useFlags();

  const {
    cartItems,
    cartUuid,
    checkoutErrorsAreVisible,
    checkoutFieldErrorStates,
    currency,
    discountCodeCodes,
    discountCodeErrors,
    discountCodes,
    dueToday,
    eSignature,
    hasAgreedToAutopay,
    hasAgreedToPaymentPolicy,
    hasAutopayPaymentOptionSelected,
    isLoading,
    paymentWaiver,
    selectedPaymentMethod,
    submissionErrors,
    submissionErrorsAreVisible,
    subtotal,
    taxAmount,
    totalAmount,
    transactionFees,
    storedPaymentMethods,
    temporaryPaymentMethod,
    updateCartItems,
    updateCheckoutErrorsAreVisible,
    updateDataAfterCartItemDeletion,
    updateDataAfterDiscountCodesUpdate,
    updateDataAfterPaymentMethodSelection,
    updateDataAfterPaymentPlanSelection,
    updateDiscountCodeErrors,
    updateESignature,
    updateESignatureError,
    updateHasAgreedToAutopay,
    updateHasAgreedToPaymentPolicy,
    updatePaymentMethodErrors,
    updatePaymentWaiver,
    updatePaymentWaiverErrors,
    updateSelectedPaymentMethod,
    updateSubmissionErrors,
    updateSubmissionErrorsAreVisible,
    updateStoredPaymentMethods,
    updateTemporaryPaymentMethod,
  } = useContext<ConsolidatedCheckoutData>(ConsolidatedCheckoutContext);
  const [updateCheckoutSummary] = useUpdateCheckoutSummaryBFFMutation();
  const [deleteCartItem] = useDeleteCartItemMutation();
  const { addMessage } = useContext(MessagingContext);

  async function handleCartItemDeletion(cartItemUuid: string) {
    // First connect to the API to delete the cart item
    await deleteCartItem({
      siteDomain: subdomain,
      cartItemId: cartItemUuid,
    })
      .unwrap()
      .then(() => {
        getCheckoutSummaryFollowingDeletion(cartItemUuid);
      })
      .catch((error) => {
        console.log('error' + JSON.stringify(error));
      });
  }

  function getCheckoutSummaryFollowingDeletion(deletedCartItemUuid: string) {
    // Delete the cart item locally before passing cartItems to the BFF
    const updatedCartItems = cartItems.filter(
      (cartItem) => cartItem.cartItemUuid !== deletedCartItemUuid
    );
    updateCheckoutSummary({
      combinedCartItems: updatedCartItems,
      discountCodes: discountCodeCodes,
      paymentMethod: selectedPaymentMethod?.paymentType || 'CARD',
      subdomain,
    })
      .unwrap()
      .then((checkoutSummaryData) => {
        updateDataAfterCartItemDeletion(checkoutSummaryData);
      })
      .catch((error) => {
        console.log('error' + JSON.stringify(error));
      });
  }

  async function handleDiscountCodeUpdateSubmission(
    discountCodeToUpdate: string,
    addOrRemove: string
  ) {
    // Early return if no cartItems in state
    if (!cartItems || cartItems.length === 0) {
      return;
    }
    if (
      addOrRemove === 'add' &&
      discountCodeCodes.includes(discountCodeToUpdate)
    ) {
      showDiscountCodeToast(
        discountCodeToUpdate,
        'duplicate' as DiscountCodeToastType
      );
    } else {
      await handleDiscountCodesUpdate(discountCodeToUpdate, addOrRemove);
    }
  }

  async function handleDiscountCodesUpdate(
    discountCodeToUpdate: string,
    addOrRemove: string
  ) {
    const paymentMethod: string = selectedPaymentMethod?.paymentType || 'CARD';
    const discountCodesToApply: string[] = discountCodes
      ? getUpdatedDiscountCodeCodes(
          discountCodes,
          discountCodeToUpdate,
          addOrRemove
        )
      : [];

    updateCheckoutSummary({
      combinedCartItems: cartItems,
      discountCodes: discountCodesToApply,
      paymentMethod,
      subdomain,
    })
      .unwrap()
      .then((response) => {
        if (
          response.messages?.some(
            (message: { level: 'INFO' | 'WARN'; message: string }) =>
              message.level === 'WARN' &&
              message.message.includes('Could not apply discount code')
          )
        ) {
          const errorMessageToDisplay = extractFormattedMessageFromError(
            response.messages
          );
          throw Error(errorMessageToDisplay, {
            cause: 'Warning from code validation response.',
          });
        }

        showDiscountCodeToast(
          discountCodeToUpdate,
          addOrRemove as DiscountCodeToastType
        );
        updateDataAfterDiscountCodesUpdate(response);
      })
      .catch((error) => {
        if (error.cause === 'Warning from code validation response.') {
          updateDiscountCodeErrors([error.message]);
        } else {
          console.log('error' + JSON.stringify(error));
        }
      });
  }

  function showDiscountCodeToast(
    discountCodeName: string,
    updateType: DiscountCodeToastType
  ) {
    let message: string;
    let variant: ToastVariant;

    switch (updateType) {
      case 'add':
        message = 'has been successfully applied.';
        variant = 'success';
        break;
      case 'duplicate':
        message = 'has already been applied.';
        variant = 'info';
        break;
      case 'remove':
        message = 'has been successfully removed.';
        variant = 'success';
        break;
      default:
        message = 'was not applied';
        variant = 'info';
        break;
    }

    addMessage('toast', {
      description: `"${discountCodeName}" ${message}`,
      variant: variant,
    });
  }

  function handlePayLaterSelection(paymentPlanId: string, payLater: boolean) {
    const updatedCartItems = updatePayLaterInCartItemPaymentPlans(
      cartItems,
      paymentPlanId,
      payLater
    );
    updateCartItems(updatedCartItems);
  }

  async function handlePaymentMethodSelection(newPaymentMethod: PaymentMethod) {
    if (newPaymentMethod === null) {
      updatePaymentMethodErrors(false);
      return;
    }

    updateSelectedPaymentMethod(newPaymentMethod);

    const paymentMethod: string = newPaymentMethod.paymentType;
    updateCheckoutSummary({
      combinedCartItems: cartItems,
      discountCodes: discountCodeCodes,
      paymentMethod,
      subdomain,
    })
      .unwrap()
      .then((checkoutSummaryData) => {
        updateDataAfterPaymentMethodSelection(checkoutSummaryData);
      });
  }

  async function handlePaymentPlanSelection(
    selectedCartItemUuid: string,
    updatedPaymentPlanSelection: PaymentPlanSummary
  ) {
    // Early return if no cartItems in state
    if (!cartItems || cartItems.length === 0) {
      return;
    }

    const updatedCartItems = updatePaymentPlansInCartItems(
      cartItems,
      selectedCartItemUuid,
      updatedPaymentPlanSelection
    );

    updateCartItems(updatedCartItems);

    const paymentMethod: string = selectedPaymentMethod?.paymentType || 'CARD';
    updateCheckoutSummary({
      combinedCartItems: updatedCartItems,
      discountCodes: discountCodeCodes,
      paymentMethod,
      subdomain,
    })
      .unwrap()
      .then((checkoutSummaryData) => {
        updateDataAfterPaymentPlanSelection(checkoutSummaryData);
      });
  }

  async function handleRegistrationRemoval(
    cartItemUuid: string,
    deletedRegistrationUuid: string,
    subprogramId: number
  ) {
    const updatedCartItems = removeRegistrationFromCartItem(
      cartItems,
      cartItemUuid,
      subprogramId,
      deletedRegistrationUuid
    );
    updateCheckoutSummary({
      combinedCartItems: updatedCartItems,
      discountCodes: discountCodeCodes,
      paymentMethod: selectedPaymentMethod?.paymentType ?? 'CARD',
      subdomain,
    })
      .unwrap()
      .then((checkoutSummaryData) => {
        updateDataAfterCartItemDeletion(checkoutSummaryData);
      })
      .catch((error) => {
        console.log('error' + JSON.stringify(error));
      });
  }

  async function handleRegistrationAddOnRemoval(
    productInstanceId: string,
    productName: string
  ) {
    const updatedCartItems = removeRegistrationAddOnFromCartItems(
      cartItems,
      productInstanceId
    );
    updateCheckoutSummary({
      combinedCartItems: updatedCartItems,
      discountCodes: discountCodeCodes,
      paymentMethod: selectedPaymentMethod?.paymentType ?? 'CARD',
      subdomain,
    })
      .unwrap()
      .then((checkoutSummaryData) => {
        updateDataAfterCartItemDeletion(checkoutSummaryData);
        addMessage('toast', {
          description: `"${productName}" has been successfully removed.`,
          variant: 'success',
        });
      })
      .catch((error) => {
        console.error('error' + JSON.stringify(error));
      });
  }

  /* Checkout Validation */
  function validatePaymentMethodWithReason(
    dueToday?: string
  ): ValidationResult {
    const dueAmount = parseDueAmount(dueToday);

    // No payment needed scenario
    if (isPaymentMethodNotRequired(dueAmount)) {
      return {
        isValid: true,
        reason: 'No payment required - zero due and no autopay',
      };
    }

    // Payment method required but not selected
    if (!selectedPaymentMethod) {
      return {
        isValid: false,
        reason: 'Payment method required but not selected',
      };
    }

    // Autopay validation
    if (hasAutopayPaymentOptionSelected) {
      if (!hasAgreedToAutopay) {
        return { isValid: false, reason: 'Autopay agreement required' };
      }

      if (isSingleUseCard(selectedPaymentMethod)) {
        return {
          isValid: false,
          reason: 'Single-use cards not allowed for autopay',
        };
      }
    }

    return { isValid: true, reason: 'All payment requirements satisfied' };
  }

  /**
   * Safely parses the due amount string to a number
   * @param dueToday - String representation of amount
   * @returns Parsed number or 0 if invalid/empty
   */
  function parseDueAmount(dueToday?: string): number {
    if (!dueToday || dueToday?.toString().trim() === '') {
      return 0;
    }

    const parsed = parseFloat(dueToday);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Determines if payment method is not required
   * @param dueAmount - Parsed due amount
   * @returns true if no payment method needed
   */
  function isPaymentMethodNotRequired(dueAmount: number): boolean {
    return dueAmount === 0 && !hasAutopayPaymentOptionSelected;
  }

  function validatePaymentWaiver() {
    if (paymentWaiver) {
      const validESignature = !ngESignature || !!eSignature?.trim();
      updateESignatureError(!validESignature);
      updateHasAgreedToPaymentPolicy(hasAgreedToPaymentPolicy);
      return hasAgreedToPaymentPolicy && validESignature;
    }

    return true;
  }

  function validateCheckout(dueToday?: string): {
    methodIsValid: boolean;
    waiverIsValid: boolean;
  } {
    const paymentMethodValidationResult =
      validatePaymentMethodWithReason(dueToday);
    if (!paymentMethodValidationResult.isValid) {
      console.log(paymentMethodValidationResult.reason);
    }
    const methodIsValid = paymentMethodValidationResult.isValid;
    const waiverIsValid = validatePaymentWaiver();
    return { methodIsValid, waiverIsValid };
  }

  function updateErrorStates(
    displayMethodError: boolean,
    displayWaiverError: boolean
  ) {
    updatePaymentMethodErrors(displayMethodError);
    updatePaymentWaiverErrors(displayWaiverError);
  }
  /* */

  return {
    cartItems,
    cartUuid,
    checkoutErrorsAreVisible,
    checkoutFieldErrorStates,
    currency,
    discountCodeNames: discountCodeCodes,
    discountCodeErrors,
    discountCodes,
    dueToday,
    eSignature,
    hasAgreedToAutopay,
    hasAutopayPaymentOptionSelected,
    hasAgreedToPaymentPolicy,
    isLoading,
    paymentWaiver,
    selectedPaymentMethod,
    submissionErrors,
    submissionErrorsAreVisible,
    subtotal,
    taxAmount,
    temporaryPaymentMethod,
    totalAmount,
    transactionFees,
    storedPaymentMethods,
    handleCartItemDeletion,
    handleDiscountCodeUpdateSubmission,
    handleESignatureUpdate: updateESignature,
    handleCheckoutErrorVisibilityUpdate: updateCheckoutErrorsAreVisible,
    handleHasAgreedToAutopayUpdate: updateHasAgreedToAutopay,
    handleHasAgreedToPaymentPolicy: updateHasAgreedToPaymentPolicy,
    handlePayLaterSelection,
    handlePaymentMethodSelection,
    handlePaymentPlanSelection,
    handlePaymentWaiverUpdate: updatePaymentWaiver,
    handleRegistrationRemoval,
    handleRegistrationAddOnRemoval,
    handleTemporaryPaymentMethodSelection: updateTemporaryPaymentMethod,
    handleStoredPaymentMethodsUpdate: updateStoredPaymentMethods,
    handleSubmissionErrorsUpdate: updateSubmissionErrors,
    handleSubmissionErrorsVisibilityUpdate: updateSubmissionErrorsAreVisible,
    updateErrorStates,
    validateCheckout,
  };
};

/* Utility Functions */
export function updatePayLaterInCartItemPaymentPlans(
  cartItems: CombinedCartItemData[],
  ngPaymentPlanId: string,
  payLater: boolean
): CombinedCartItemData[] {
  // Create a deep copy of the cart items to avoid mutating the original
  const updatedCartItems = JSON.parse(
    JSON.stringify(cartItems)
  ) as CombinedCartItemData[];

  for (const cartItem of updatedCartItems) {
    // Update in paymentPlanSummaries
    if (cartItem.paymentPlanSummaries) {
      for (const planSummary of cartItem.paymentPlanSummaries) {
        if (planSummary.ngPaymentPlanId === ngPaymentPlanId) {
          planSummary.payLater = payLater;
        }
      }
    }
    // Update in selectedPaymentPlan if it matches
    if (cartItem.selectedPaymentPlan.ngPaymentPlanId === ngPaymentPlanId) {
      cartItem.selectedPaymentPlan.payLater = payLater;
    }
  }
  return updatedCartItems;
}

export function updateDiscountCodes(
  state: ConsolidatedCheckoutData,
  discountCode: DiscountCode,
  action: 'add' | 'remove'
): ConsolidatedCheckoutData {
  const { discountCodes = [] } = state;
  const newDiscountCodes =
    action === 'add'
      ? [...discountCodes, discountCode]
      : discountCodes.filter((code) => code.code !== discountCode.code);
  return { ...state, discountCodes: newDiscountCodes };
}

export function updatePaymentPlansInCartItems(
  cartItems: CombinedCartItemData[],
  selectedCartItemUuid: string,
  updatedPaymentPlanSelection: PaymentPlanSummary
) {
  const cartItemsToUpdate = [...cartItems];
  const cartItemToUpdate = cartItemsToUpdate.findIndex(
    (cartItem) => cartItem.cartItemUuid === selectedCartItemUuid
  );
  const updatedCartItem = {
    ...cartItemsToUpdate[cartItemToUpdate],
    paymentTerm: getPaymentTermFromPaymentPlan(updatedPaymentPlanSelection),
    selectedPaymentPlan: updatedPaymentPlanSelection,
  };
  cartItemsToUpdate.splice(cartItemToUpdate, 1, updatedCartItem);
  return cartItemsToUpdate;
}

export function getPaymentTermFromPaymentPlan(
  paymentPlan: PaymentPlanSummary
): PaymentTerm {
  return paymentPlan.installments && paymentPlan.installments.length > 0
    ? 'PAYMENT_PLAN'
    : 'FULL';
}

export function getDefaultPaymentMethod(
  storedPaymentMethods: PaymentMethodInfo
) {
  if (!storedPaymentMethods) {
    return null;
  }

  const { paymentOptionsCount, storedCreditCards, storedBankAccounts } =
    storedPaymentMethods;
  if (paymentOptionsCount === 0) {
    return null;
  } else if (
    storedCreditCards.some(
      (storedCreditCard) => storedCreditCard.isPrimaryPaymentOption
    )
  ) {
    return storedCreditCards.find(
      (storedCreditCard) => storedCreditCard.isPrimaryPaymentOption
    );
  } else if (
    storedBankAccounts.some(
      (storedBankAccount) => storedBankAccount.isPrimaryPaymentOption
    )
  ) {
    return storedBankAccounts.find(
      (storedBankAccount) => storedBankAccount.isPrimaryPaymentOption
    );
  }
  return null;
}

export function getUpdatedDiscountCodeCodes(
  discountCodes: DiscountCode[],
  discountCodeToUpdate: string,
  addOrRemove: string
) {
  if (addOrRemove === 'add') {
    return [
      ...discountCodes.map((discountCode) => discountCode.code),
      discountCodeToUpdate,
    ];
  } else {
    return discountCodes
      .filter((discountCode) => discountCode.code !== discountCodeToUpdate)
      .map((discountCode) => discountCode.code);
  }
}

export function getUniqueDiscountCodeCodes(discountCodes: DiscountCode[]) {
  const discountCodeNames: string[] = [];
  for (const discountCode of discountCodes) {
    if (!discountCodeNames.includes(discountCode.code)) {
      discountCodeNames.push(discountCode.code);
    }
  }
  return discountCodeNames;
}

export function cartItemsIncludeAutopayPaymentPlan(
  cartItems: CombinedCartItemData[]
) {
  const selectedPaymentPlans = cartItems.map(
    (cartItem) => cartItem.selectedPaymentPlan
  );
  return selectedPaymentPlans.some(
    (selectedPaymentPlan) => selectedPaymentPlan.autopayRequired
  );
}

export function extractFormattedMessageFromError(
  messages: { level: 'INFO' | 'WARN'; message: string }[]
) {
  const warningMessage = messages.find((message) => message.level === 'WARN');
  if (warningMessage?.message) {
    const discountCode = extractDiscountCode(warningMessage.message);
    return `Could not apply ${discountCode} because it is invalid or it has expired.`;
  }
  return 'This code is invalid or expired.';
}

/**
 * Extracts a discount code from an error message string
 * @param errorMessage - The error message containing the discount code
 * @returns The extracted discount code or null if not found
 */
function extractDiscountCode(errorMessage: string): string | null {
  // Using regex to match the pattern "discount code X because"
  const regex = /could not apply discount code\s+(\S+)\s+because/i;
  const match = regex.exec(errorMessage);

  // Return the captured group (the discount code) if found, otherwise null
  return match ? match[1] : null;
}

export {
  ConsolidatedCheckoutContext,
  ConsolidatedCheckoutProvider,
  useConsolidatedCheckout,
};

export function removeRegistrationAddOnFromCartItems(
  cartItems: CombinedCartItemData[],
  productInstanceId: string
): CombinedCartItemData[] {
  return (
    cartItems
      .map((cartItem) => {
        if (!Array.isArray(cartItem.addOns) || cartItem.addOns.length === 0) {
          return cartItem;
        }

        const updatedAddOns = cartItem.addOns.filter((addOn) => {
          return addOn.productInstanceId !== productInstanceId;
        });

        if (updatedAddOns.length === cartItem.addOns.length) {
          return cartItem;
        }

        return { ...cartItem, addOns: updatedAddOns } as CombinedCartItemData;
      })
      // return cartItem objects that have no addOns array or addOns arrays with at least one item
      .filter((cartItem) => {
        return !Array.isArray(cartItem.addOns) || cartItem.addOns.length > 0;
      })
  );
}

/**
 * Removes a specific registration from a subprogram within a cart item.
 * If removing the registration results in an empty registrations array,
 * the entire cart item will be removed.
 *
 * @param cartItems - The array of cart items
 * @param cartItemUuid - The UUID of the cart item containing the subprogram
 * @param subprogramId - The ID of the subprogram containing the registration
 * @param registrationId - The ID of the registration to remove
 * @returns A new array of cart items with the specified registration removed
 */
export function removeRegistrationFromCartItem(
  cartItems: CombinedCartItemData[],
  cartItemUuid: string,
  subprogramId: number,
  registrationId: string
): CombinedCartItemData[] {
  // Process each cart item
  const updatedCartItems = cartItems.map((cartItem) => {
    // Skip if this is not the target cart item
    if (cartItem.cartItemUuid !== cartItemUuid) {
      return cartItem;
    }

    // If the cart item doesn't have subprograms, return it unchanged
    if (!cartItem.subprograms || cartItem.subprograms.length === 0) {
      return cartItem;
    }

    // Update subprograms for the target cart item
    const updatedSubprograms = cartItem.subprograms.map((subprogram) => {
      // Skip if this is not the target subprogram
      if (subprogram.subprogramId !== subprogramId) {
        return subprogram;
      }

      // Filter out the target registration
      const updatedRegistrations = subprogram.registrations.filter(
        (registration) => registration.registrationId !== registrationId
      );

      return {
        ...subprogram,
        registrations: updatedRegistrations,
      };
    });

    // Check if all subprograms in the cart item have empty registrations arrays
    const hasNoRegistrations = updatedSubprograms.every(
      (subprogram) => subprogram.registrations.length === 0
    );

    // If we have no registrations in any of the cart item's subprograms,
    // mark this cart item for removal by returning null
    if (hasNoRegistrations) {
      return null;
    }

    return {
      ...cartItem,
      subprograms: updatedSubprograms,
    };
  });

  // Filter out any null values (cart items marked for removal)
  return updatedCartItems.filter(
    (item) => item !== null
  ) as CombinedCartItemData[];
}
